import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkle, EnvelopeSimple, MapPinPlus, Check, X } from "@phosphor-icons/react";
import { useAuth } from "../lib/AuthContext";
import { useSavedLocations } from "../lib/SavedLocationsContext";
import { api } from "../lib/api";

/**
 * OnboardingCard — first-login prompt with two checklist items:
 * 1. Confirm your email
 * 2. Add a saved location
 *
 * Auto-hides once both done, or when the user dismisses. Dismissal is
 * persisted per-user in localStorage so it doesn't reappear on every visit.
 */
export default function OnboardingCard({ onAddLocation }) {
  const { user } = useAuth();
  const { locations } = useSavedLocations();
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(false);
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);

  const dismissKey = user && user !== false ? `firewatch:onboarding-dismissed:${user.id}` : null;

  useEffect(() => {
    if (!dismissKey) return;
    setDismissed(localStorage.getItem(dismissKey) === "1");
  }, [dismissKey]);

  if (!user || user === false) return null;
  if (user.role === "admin") return null;
  if (dismissed) return null;

  const emailDone = !!user.email_verified;
  const locationDone = locations.length > 0;
  if (emailDone && locationDone) return null;

  const dismiss = () => {
    if (dismissKey) localStorage.setItem(dismissKey, "1");
    setDismissed(true);
  };

  const resendVerification = async () => {
    setResending(true);
    try {
      await api.post("/auth/request-verification");
      setResent(true);
    } catch {
      /* banner already covers retry */
    } finally {
      setResending(false);
    }
  };

  return (
    <div
      data-testid="onboarding-card"
      className="border-b border-tac-primary/30 bg-gradient-to-r from-tac-primary/8 via-tac-primary/5 to-transparent px-4 py-3"
    >
      <div className="flex items-start gap-3">
        <Sparkle size={18} weight="fill" className="text-tac-primary shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 mb-1.5">
            <h3 className="font-display font-bold text-sm text-tac-text">
              Welcome, {user.display_name.split(" ")[0]}. Two quick things —
            </h3>
            <button
              data-testid="onboarding-dismiss"
              onClick={dismiss}
              className="text-tac-faint hover:text-tac-text shrink-0"
              aria-label="Dismiss"
            >
              <X size={14} weight="bold" />
            </button>
          </div>
          <ul className="space-y-2 mt-2">
            <Step
              done={emailDone}
              icon={<EnvelopeSimple size={12} weight="bold" />}
              text={emailDone ? `Email confirmed (${user.email})` : `Confirm ${user.email}`}
              action={
                !emailDone && (
                  <button
                    data-testid="onboarding-resend-email"
                    onClick={resendVerification}
                    disabled={resending || resent}
                    className="text-[10px] font-bold uppercase tracking-wider text-tac-primary hover:underline disabled:opacity-50"
                  >
                    {resent ? "Sent — check inbox" : resending ? "Sending…" : "Resend link"}
                  </button>
                )
              }
            />
            <Step
              done={locationDone}
              icon={<MapPinPlus size={12} weight="bold" />}
              text={
                locationDone
                  ? `${locations.length} saved location${locations.length === 1 ? "" : "s"} — push alerts ready`
                  : "Pin home/cabin/work so we can warn you when fires get close"
              }
              action={
                !locationDone && (
                  <button
                    data-testid="onboarding-add-location"
                    onClick={onAddLocation}
                    className="text-[10px] font-bold uppercase tracking-wider text-tac-primary hover:underline"
                  >
                    Add location
                  </button>
                )
              }
            />
          </ul>
        </div>
      </div>
    </div>
  );
}

function Step({ done, icon, text, action }) {
  return (
    <li className="flex items-center gap-2 text-xs">
      <span
        className={`w-4 h-4 rounded-full border flex items-center justify-center shrink-0 ${
          done
            ? "bg-tac-primary border-tac-primary text-white"
            : "border-tac-border text-tac-faint"
        }`}
      >
        {done ? <Check size={9} weight="bold" /> : icon}
      </span>
      <span className={`flex-1 min-w-0 truncate ${done ? "text-tac-dim line-through decoration-tac-dim/60" : "text-tac-text"}`}>
        {text}
      </span>
      {action}
    </li>
  );
}
